import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createMatrixBoardBase, createMatrixCellFrame } from "../../scenes/matrixPrimitives";
import { cellKey, getGridCellPosition, isInsideGrid, normalizeNumericGridInput } from "../../scenes/shared/gridUtils";

const CELL_SIZE = 1.18;
const CELL_GAP = 0.2;
const CELL_SPACING = CELL_SIZE + CELL_GAP;
const WATER_HEIGHT = 0.12;
const LAND_HEIGHT = 0.34;

const WATER_COLOR = new THREE.Color(0x0f2747);
const WATER_ACTIVE_COLOR = new THREE.Color(0x38bdf8);
const LAND_IDLE_COLOR = new THREE.Color(0x3f7c61);
const LAND_ACTIVE_COLOR = new THREE.Color(0xf8fafc);
const LAND_PROCESSED_BRIGHTEN = new THREE.Color(0xffffff);

const ISLAND_COLORS = [
    new THREE.Color(0x22c55e),
    new THREE.Color(0xf59e0b),
    new THREE.Color(0xa78bfa),
    new THREE.Color(0xf472b6),
    new THREE.Color(0x06b6d4),
    new THREE.Color(0xfb7185),
];

const NEIGHBOR_OFFSETS = [
    { dr: -1, dc: 0, label: "up" },
    { dr: 0, dc: 1, label: "right" },
    { dr: 1, dc: 0, label: "down" },
    { dr: 0, dc: -1, label: "left" },
];

function getIslandColor(islandId) {
    return ISLAND_COLORS[(islandId - 1) % ISLAND_COLORS.length];
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1;
    const location = `[${step.row}, ${step.col}]`;

    switch (step.type) {
        case "scan-cell":
            return {
                id: `number-of-islands-step-${stepNumber}-scan-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Scan",
                title: `Scan cell ${location}`,
                message:
                    step.value === 1
                        ? `The top-level row scan reaches land at ${location}. If it has not been visited yet, this starts a new island BFS.`
                        : `The top-level row scan reaches water at ${location}, so the algorithm moves on immediately.`,
            };

        case "skip-visited-land":
            return {
                id: `number-of-islands-step-${stepNumber}-skip-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Skip",
                title: `Skip visited land ${location}`,
                message: `Cell ${location} is land, but it already belongs to island ${step.islandId}, so no new BFS is started.`,
            };

        case "start-island":
            return {
                id: `number-of-islands-step-${stepNumber}-start-island-${step.islandId}`,
                stepIndex: stepNumber,
                kind: "Island",
                title: `Start island ${step.islandId}`,
                message: `Discovers a new connected component at ${location} and increments the island count to ${step.islandId}.`,
            };

        case "enqueue-cell":
            return {
                id: `number-of-islands-step-${stepNumber}-enqueue-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Queue",
                title: `Enqueue cell ${location}`,
                message: step.isStart
                    ? `Seeds island ${step.islandId}'s BFS queue with starting land cell ${location}.`
                    : `Adds newly discovered land cell ${location} to island ${step.islandId}'s BFS queue after reaching it from [${step.fromRow}, ${step.fromCol}].`,
            };

        case "dequeue-cell":
            return {
                id: `number-of-islands-step-${stepNumber}-dequeue-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Queue",
                title: `Dequeue cell ${location}`,
                message: `Processes the next frontier cell for island ${step.islandId} by removing ${location} from the BFS queue.`,
            };

        case "inspect-neighbor": {
            let message = `Checks the ${step.direction} neighbor of ${location}. `;

            if (step.outcome === "out-of-bounds") {
                message += "That neighbor is outside the grid, so it cannot contribute to the current island.";
            } else if (step.outcome === "water") {
                message += `Neighbor [${step.neighborRow}, ${step.neighborCol}] is water, so BFS does not expand through it.`;
            } else if (step.outcome === "visited") {
                message += `Neighbor [${step.neighborRow}, ${step.neighborCol}] is land, but it is already assigned to island ${step.neighborIslandId}.`;
            } else {
                message += `Neighbor [${step.neighborRow}, ${step.neighborCol}] is new land, so it becomes part of island ${step.islandId}.`;
            }

            return {
                id: `number-of-islands-step-${stepNumber}-inspect-${step.row}-${step.col}-${step.direction}`,
                stepIndex: stepNumber,
                kind: "Inspect",
                title: `Inspect ${step.direction} neighbor of ${location}`,
                message,
            };
        }

        case "finish-cell":
            return {
                id: `number-of-islands-step-${stepNumber}-finish-cell-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Finish",
                title: `Finish cell ${location}`,
                message: `All four neighbors of ${location} have been checked, so this BFS cell is fully processed.`,
            };

        case "finish-island":
            return {
                id: `number-of-islands-step-${stepNumber}-finish-island-${step.islandId}`,
                stepIndex: stepNumber,
                kind: "Island",
                title: `Finish island ${step.islandId}`,
                message: `The BFS queue is empty, so island ${step.islandId} is fully explored and the scan returns to the grid walk.`,
            };

        case "complete":
            return {
                id: `number-of-islands-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Done",
                title: "Count complete",
                message: `The full grid scan has completed. Total islands found: ${step.islandCount}.`,
            };

        default:
            return {
                id: `number-of-islands-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the flood-fill visualization to the next state.",
            };
    }
}

const DEFAULT_GRID = [
    [1, 1, 0, 0],
    [1, 0, 0, 1],
    [0, 0, 1, 1],
    [1, 0, 0, 0],
];

function getCellValue(grid, row, col) {
    if (!isInsideGrid(grid, row, col)) {
        return 0;
    }

    return grid[row][col];
}

function buildIslandSteps(grid) {
    const visited = new Set();
    const islandIdsByCell = new Map();
    const steps = [];
    let islandCount = 0;

    for (let row = 0; row < grid.length; row += 1) {
        for (let col = 0; col < grid[row].length; col += 1) {
            const key = cellKey(row, col);
            const value = grid[row][col];

            steps.push({
                type: "scan-cell",
                row,
                col,
                value,
            });

            if (value === 0) {
                continue;
            }

            if (visited.has(key)) {
                steps.push({
                    type: "skip-visited-land",
                    row,
                    col,
                    islandId: islandIdsByCell.get(key),
                });
                continue;
            }

            islandCount += 1;
            steps.push({
                type: "start-island",
                row,
                col,
                islandId: islandCount,
            });

            const queue = [[row, col]];
            visited.add(key);
            islandIdsByCell.set(key, islandCount);

            steps.push({
                type: "enqueue-cell",
                row,
                col,
                islandId: islandCount,
                isStart: true,
            });

            while (queue.length > 0) {
                const [currentRow, currentCol] = queue.shift();

                steps.push({
                    type: "dequeue-cell",
                    row: currentRow,
                    col: currentCol,
                    islandId: islandCount,
                });

                for (const direction of NEIGHBOR_OFFSETS) {
                    const neighborRow = currentRow + direction.dr;
                    const neighborCol = currentCol + direction.dc;
                    const neighborKey = cellKey(neighborRow, neighborCol);

                    let outcome = "out-of-bounds";
                    let neighborIslandId = null;

                    if (isInsideGrid(grid, neighborRow, neighborCol)) {
                        const neighborValue = getCellValue(grid, neighborRow, neighborCol);

                        if (neighborValue === 0) {
                            outcome = "water";
                        } else if (visited.has(neighborKey)) {
                            outcome = "visited";
                            neighborIslandId = islandIdsByCell.get(neighborKey);
                        } else {
                            outcome = "land";
                        }
                    }

                    steps.push({
                        type: "inspect-neighbor",
                        row: currentRow,
                        col: currentCol,
                        direction: direction.label,
                        neighborRow,
                        neighborCol,
                        outcome,
                        islandId: islandCount,
                        neighborIslandId,
                    });

                    if (outcome === "land") {
                        visited.add(neighborKey);
                        islandIdsByCell.set(neighborKey, islandCount);
                        queue.push([neighborRow, neighborCol]);

                        steps.push({
                            type: "enqueue-cell",
                            row: neighborRow,
                            col: neighborCol,
                            fromRow: currentRow,
                            fromCol: currentCol,
                            islandId: islandCount,
                            isStart: false,
                        });
                    }
                }

                steps.push({
                    type: "finish-cell",
                    row: currentRow,
                    col: currentCol,
                    islandId: islandCount,
                });
            }

            steps.push({
                type: "finish-island",
                islandId: islandCount,
            });
        }
    }

    steps.push({
        type: "complete",
        islandCount,
    });

    return steps;
}

export default class NumberOfIslandsScene extends BaseThreeScene {
    setup(props) {
        this.grid = normalizeNumericGridInput(props.input, DEFAULT_GRID);
        this.steps = buildIslandSteps(this.grid);

        const maxDimension = Math.max(this.grid.length, this.grid[0].length);
        const cameraHeight = 4.4 + maxDimension * 0.68;
        const cameraDepth = 4.9 + maxDimension * 0.62;

        this.scene.background = new THREE.Color(0x040816);
        this.scene.fog = new THREE.Fog(0x040816, 9, 20);
        this.camera.position.set(0, cameraHeight, cameraDepth);
        this.camera.lookAt(0, 0.2, 0);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.9);
        keyLight.position.set(5, 8, 4);
        this.scene.add(keyLight);

        const fillLight = new THREE.PointLight(0x38bdf8, 16, 18, 2);
        fillLight.position.set(-5, 3.5, 4);
        this.scene.add(fillLight);

        const accentLight = new THREE.PointLight(0x22c55e, 10, 14, 2);
        accentLight.position.set(4, 3, -2);
        this.scene.add(accentLight);

        this.boardRoot = new THREE.Group();
        this.scene.add(this.boardRoot);

        this.cellMeshes = new Map();
        this.cellPositions = new Map();
        this.presentationTime = 0;

        this.buildBoard();
        this.buildIndicators();
        this.resetAnimationState();
        this.advanceStep();
    }

    buildBoard() {
        const rows = this.grid.length;
        const cols = this.grid[0].length;
        const boardWidth = cols * CELL_SPACING + 0.9;
        const boardDepth = rows * CELL_SPACING + 0.9;

        const boardBase = createMatrixBoardBase({
            width: boardWidth,
            depth: boardDepth,
            baseHeight: 0.18,
            emissiveIntensity: 0.35,
            roughness: 0.75,
            metalness: 0.08,
            surfaceInset: 0.16,
            surfaceOpacity: 0.8,
            baseY: -0.12,
            surfaceY: -0.02,
        });
        this.boardRoot.add(boardBase);

        const cellGeometry = new THREE.BoxGeometry(CELL_SIZE, 1, CELL_SIZE);

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const key = cellKey(row, col);
                const isLand = this.grid[row][col] === 1;
                const position = getGridCellPosition(
                    row,
                    col,
                    rows,
                    cols,
                    CELL_SPACING
                );

                const mesh = new THREE.Mesh(
                    cellGeometry,
                    new THREE.MeshStandardMaterial({
                        color: isLand
                            ? LAND_IDLE_COLOR.getHex()
                            : WATER_COLOR.getHex(),
                        emissive: isLand
                            ? LAND_IDLE_COLOR.getHex()
                            : WATER_COLOR.getHex(),
                        emissiveIntensity: isLand ? 0.2 : 0.12,
                        roughness: isLand ? 0.52 : 0.36,
                        metalness: isLand ? 0.1 : 0.16,
                    })
                );

                const initialHeight = isLand ? LAND_HEIGHT : WATER_HEIGHT;
                mesh.position.copy(position);
                mesh.position.y = initialHeight * 0.5;
                mesh.scale.set(1, initialHeight, 1);
                mesh.userData = {
                    kind: "grid-cell",
                    row,
                    col,
                    baseX: position.x,
                    baseZ: position.z,
                };

                this.cellMeshes.set(key, mesh);
                this.cellPositions.set(key, position);
                this.boardRoot.add(mesh);
            }
        }
    }

    buildIndicators() {
        this.scanFrame = createMatrixCellFrame(CELL_SIZE + 0.26, 0x7dd3fc);
        this.boardRoot.add(this.scanFrame);

        this.activeFrame = createMatrixCellFrame(CELL_SIZE + 0.18, 0xffffff);
        this.boardRoot.add(this.activeFrame);

        this.neighborFrame = createMatrixCellFrame(CELL_SIZE + 0.1, 0xfde68a);
        this.boardRoot.add(this.neighborFrame);

        this.discoveryBeamGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(),
            new THREE.Vector3(),
        ]);
        this.discoveryBeam = new THREE.Line(
            this.discoveryBeamGeometry,
            new THREE.LineBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0,
            })
        );
        this.boardRoot.add(this.discoveryBeam);

        this.islandPulse = new THREE.Mesh(
            new THREE.RingGeometry(0.24, 0.34, 48),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0,
                side: THREE.DoubleSide,
            })
        );
        this.islandPulse.rotation.x = -Math.PI * 0.5;
        this.boardRoot.add(this.islandPulse);
    }

    resetAnimationState() {
        this.steps = buildIslandSteps(this.grid);
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;

        this.activeScanCellKey = null;
        this.activeCellKey = null;
        this.activeNeighborCellKey = null;
        this.activeNeighborOutcome = null;
        this.activeIslandId = null;

        this.visitedCells = new Set();
        this.queuedCells = new Set();
        this.processedCells = new Set();
        this.cellIslandIds = new Map();
        this.completedIslandIds = new Set();
        this.currentIslandCount = 0;
        this.islandCount = 0;
        this.activeCell = null;
        this.frontier = [];
        this.visited = new Set();

        this.replaceUiState({
            islandCount: 0,
            activeCellLabel: "-",
            frontierSize: 0,
        });
        this.discoveryBeamState = {
            active: false,
            from: new THREE.Vector3(),
            to: new THREE.Vector3(),
            elapsed: 0,
            duration: 0.55,
        };

        this.islandPulseState = {
            active: false,
            center: new THREE.Vector3(),
            color: new THREE.Color(0xffffff),
            elapsed: 0,
            duration: 0.9,
        };

        this.presentationTime = 0;
        this.resetStepLog([]);
        this.syncPlaybackState();
    }
    syncUiState() {
        this.replaceUiState({
            islandCount: this.islandCount,
            activeCellLabel: this.activeCell
                ? `(${this.activeCell.row}, ${this.activeCell.col})`
                : "-",
            frontierSize: this.frontier.length,
        });
    }
    getStepDuration(step) {
        switch (step.type) {
            case "scan-cell":
                return 0.42;
            case "skip-visited-land":
                return 0.4;
            case "start-island":
                return 0.78;
            case "enqueue-cell":
                return 0.42;
            case "dequeue-cell":
                return 0.5;
            case "inspect-neighbor":
                return 0.42;
            case "finish-cell":
                return 0.36;
            case "finish-island":
                return 0.7;
            case "complete":
                return 1.8;
            default:
                return 0.45;
        }
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;
        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex));
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        this.activeNeighborCellKey = null;
        this.activeNeighborOutcome = null;

        switch (step.type) {
            case "scan-cell": {
                this.activeScanCellKey = cellKey(step.row, step.col);
                this.activeCellKey = null;
                this.activeIslandId = null;
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);
                break;
            }

            case "skip-visited-land": {
                this.activeScanCellKey = cellKey(step.row, step.col);
                this.activeCellKey = cellKey(step.row, step.col);
                this.activeIslandId = step.islandId;
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);
                break;
            }

            case "start-island": {
                this.activeScanCellKey = cellKey(step.row, step.col);
                this.activeCellKey = cellKey(step.row, step.col);
                this.activeIslandId = step.islandId;
                this.currentIslandCount = Math.max(
                    this.currentIslandCount,
                    step.islandId
                );
                this.islandCount = this.currentIslandCount;
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);
                this.startIslandPulse(step.row, step.col, step.islandId);
                break;
            }

            case "enqueue-cell": {
                const key = cellKey(step.row, step.col);
                this.visitedCells.add(key);
                this.queuedCells.add(key);
                this.cellIslandIds.set(key, step.islandId);
                this.activeCellKey = key;
                this.activeIslandId = step.islandId;
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);
                if (!step.isStart) {
                    this.startDiscoveryBeam(
                        step.fromRow,
                        step.fromCol,
                        step.row,
                        step.col,
                        step.islandId
                    );
                }
                break;
            }

            case "dequeue-cell": {
                const key = cellKey(step.row, step.col);
                this.activeCellKey = key;
                this.activeIslandId = step.islandId;
                this.queuedCells.delete(key);
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);
                break;
            }

            case "inspect-neighbor": {
                this.activeCellKey = cellKey(step.row, step.col);
                this.activeIslandId = step.islandId;
                this.activeNeighborOutcome = step.outcome;
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);

                if (isInsideGrid(step.neighborRow, step.neighborCol)) {
                    this.activeNeighborCellKey = cellKey(
                        step.neighborRow,
                        step.neighborCol
                    );
                }

                if (step.outcome === "discover-land") {
                    this.activeNeighborOutcome = "discover-land";
                }
                break;
            }

            case "finish-cell": {
                const key = cellKey(step.row, step.col);
                this.processedCells.add(key);
                this.activeCellKey = key;
                this.activeIslandId = step.islandId;
                this.activeCell = { row: step.row, col: step.col };
                this.frontier = Array.from(this.queuedCells);
                break;
            }

            case "finish-island": {
                this.completedIslandIds.add(step.islandId);
                this.activeCellKey = null;
                this.activeIslandId = step.islandId;
                this.activeCell = null;
                this.frontier = Array.from(this.queuedCells);
                this.islandCount = this.currentIslandCount;
                break;
            }

            case "complete": {
                this.activeScanCellKey = null;
                this.activeCellKey = null;
                this.activeNeighborCellKey = null;
                this.activeNeighborOutcome = null;
                this.activeIslandId = null;
                this.activeCell = null;
                this.frontier = [];
                this.islandCount = step.islandCount;
                break;
            }

            default:
                break;
        }
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    startDiscoveryBeam(fromRow, fromCol, toRow, toCol, islandId) {
        this.discoveryBeamState.active = true;
        this.discoveryBeamState.elapsed = 0;
        this.discoveryBeamState.from.copy(this.cellPositions.get(cellKey(fromRow, fromCol)));
        this.discoveryBeamState.to.copy(this.cellPositions.get(cellKey(toRow, toCol)));
        this.discoveryBeam.material.color.copy(getIslandColor(islandId));
    }

    startIslandPulse(row, col, islandId) {
        this.islandPulseState.active = true;
        this.islandPulseState.elapsed = 0;
        this.islandPulseState.center.copy(this.cellPositions.get(cellKey(row, col)));
        this.islandPulseState.color.copy(getIslandColor(islandId));
        this.islandPulse.material.color.copy(getIslandColor(islandId));
    }

    update(delta) {
        if (!this.boardRoot || !this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();
            if (!advanced) {
                this.pause();
            }
        }

        this.updateBoardMotion(this.presentationTime);
        this.updateDiscoveryBeam(playbackDelta);
        this.updateIslandPulse(playbackDelta);
        this.updateCells(this.presentationTime);
        this.updateFrames(this.presentationTime);
    }

    updateBoardMotion(elapsed) {
        this.boardRoot.rotation.y = Math.sin(elapsed * 0.24) * 0.08;
        this.boardRoot.position.y = Math.sin(elapsed * 0.7) * 0.03;
    }

    updateDiscoveryBeam(delta) {
        const state = this.discoveryBeamState;
        const positions = this.discoveryBeamGeometry.attributes.position.array;

        if (!state.active) {
            this.discoveryBeam.material.opacity = THREE.MathUtils.lerp(
                this.discoveryBeam.material.opacity,
                0,
                0.18
            );
            return;
        }

        state.elapsed += delta;
        const t = Math.min(state.elapsed / state.duration, 1);
        const eased = THREE.MathUtils.smoothstep(t, 0, 1);
        const currentEnd = new THREE.Vector3().lerpVectors(
            state.from,
            state.to,
            eased
        );

        positions[0] = state.from.x;
        positions[1] = LAND_HEIGHT + 0.18;
        positions[2] = state.from.z;
        positions[3] = currentEnd.x;
        positions[4] = LAND_HEIGHT + 0.18;
        positions[5] = currentEnd.z;
        this.discoveryBeamGeometry.attributes.position.needsUpdate = true;
        this.discoveryBeam.material.opacity = 0.95;

        if (t >= 1) {
            state.active = false;
        }
    }

    updateIslandPulse(delta) {
        const state = this.islandPulseState;

        if (!state.active) {
            this.islandPulse.material.opacity = THREE.MathUtils.lerp(
                this.islandPulse.material.opacity,
                0,
                0.16
            );
            return;
        }

        state.elapsed += delta;
        const t = Math.min(state.elapsed / state.duration, 1);
        const eased = THREE.MathUtils.smoothstep(t, 0, 1);

        this.islandPulse.position.set(state.center.x, 0.04, state.center.z);
        this.islandPulse.scale.setScalar(0.8 + eased * 2.6);
        this.islandPulse.material.opacity = 0.85 * (1 - eased);

        if (t >= 1) {
            state.active = false;
        }
    }

    updateCells(elapsed) {
        this.cellMeshes.forEach((mesh, key) => {
            const [rowString, colString] = key.split(":");
            const row = Number(rowString);
            const col = Number(colString);
            const isLand = this.grid[row][col] === 1;
            const islandId = this.cellIslandIds.get(key) || null;
            const isQueued = this.queuedCells.has(key);
            const isProcessed = this.processedCells.has(key);
            const isActiveCell = key === this.activeCellKey;
            const isActiveScan = key === this.activeScanCellKey;
            const isActiveNeighbor = key === this.activeNeighborCellKey;

            let targetColor = isLand ? LAND_IDLE_COLOR.clone() : WATER_COLOR.clone();
            let targetHeight = isLand ? LAND_HEIGHT : WATER_HEIGHT;
            let targetScaleXZ = 1;
            let targetEmissiveIntensity = isLand ? 0.22 : 0.12;

            if (isLand && islandId) {
                targetColor.copy(getIslandColor(islandId));
                targetEmissiveIntensity = 0.32;
            }

            if (isProcessed && isLand && islandId) {
                targetColor.lerp(LAND_PROCESSED_BRIGHTEN, 0.16);
                targetEmissiveIntensity = 0.38;
            }

            if (isQueued) {
                targetHeight += 0.04;
                targetScaleXZ = 1.04;
                targetEmissiveIntensity = Math.max(targetEmissiveIntensity, 0.48);
            }

            if (isActiveNeighbor) {
                if (this.activeNeighborOutcome === "water") {
                    targetColor.copy(WATER_ACTIVE_COLOR);
                } else if (this.activeNeighborOutcome === "visited") {
                    targetColor.lerp(LAND_ACTIVE_COLOR, 0.28);
                } else if (this.activeNeighborOutcome === "discover-land") {
                    targetColor.copy(LAND_ACTIVE_COLOR);
                }
                targetHeight += 0.03;
                targetScaleXZ = Math.max(targetScaleXZ, 1.05);
                targetEmissiveIntensity = Math.max(targetEmissiveIntensity, 0.6);
            }

            if (isActiveScan) {
                targetHeight += 0.03;
                targetScaleXZ = Math.max(targetScaleXZ, 1.05);
                targetEmissiveIntensity = Math.max(targetEmissiveIntensity, 0.45);
            }

            if (isActiveCell) {
                targetColor.copy(LAND_ACTIVE_COLOR);
                targetHeight += 0.08;
                targetScaleXZ = 1.08 + Math.sin(elapsed * 5.0) * 0.02;
                targetEmissiveIntensity = 0.92;
            }

            const targetY = targetHeight * 0.5;
            mesh.position.y = THREE.MathUtils.lerp(mesh.position.y, targetY, 0.14);
            mesh.scale.x = THREE.MathUtils.lerp(mesh.scale.x, targetScaleXZ, 0.14);
            mesh.scale.z = THREE.MathUtils.lerp(mesh.scale.z, targetScaleXZ, 0.14);
            mesh.scale.y = THREE.MathUtils.lerp(mesh.scale.y, targetHeight, 0.14);
            mesh.material.color.lerp(targetColor, 0.14);
            mesh.material.emissive.lerp(targetColor, 0.14);
            mesh.material.emissiveIntensity = THREE.MathUtils.lerp(
                mesh.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.14
            );
        });
    }

    updateFrames(elapsed) {
        this.updateFrame(
            this.scanFrame,
            this.activeScanCellKey,
            LAND_HEIGHT + 0.06,
            0.72,
            elapsed,
            0x7dd3fc
        );

        this.updateFrame(
            this.activeFrame,
            this.activeCellKey,
            LAND_HEIGHT + 0.1,
            0.95,
            elapsed,
            this.activeIslandId ? getIslandColor(this.activeIslandId).getHex() : 0xffffff
        );

        const neighborColor =
            this.activeNeighborOutcome === "water"
                ? WATER_ACTIVE_COLOR.getHex()
                : this.activeNeighborOutcome === "discover-land"
                    ? 0xffffff
                    : 0xfde68a;

        this.updateFrame(
            this.neighborFrame,
            this.activeNeighborCellKey,
            LAND_HEIGHT + 0.14,
            this.activeNeighborCellKey ? 0.84 : 0,
            elapsed,
            neighborColor
        );
    }

    updateFrame(frame, key, y, targetOpacity, elapsed, colorHex) {
        if (!key || !this.cellPositions.has(key)) {
            frame.material.opacity = THREE.MathUtils.lerp(
                frame.material.opacity,
                0,
                0.16
            );
            return;
        }

        const position = this.cellPositions.get(key);
        frame.position.set(position.x, y, position.z);
        frame.rotation.y = Math.sin(elapsed * 1.6) * 0.05;
        frame.material.color.setHex(colorHex);
        frame.material.opacity = THREE.MathUtils.lerp(
            frame.material.opacity,
            targetOpacity,
            0.16
        );
    }

    onDispose() {
        this.boardRoot = null;
        this.cellMeshes = null;
        this.cellPositions = null;
        this.scanFrame = null;
        this.activeFrame = null;
        this.neighborFrame = null;
        this.discoveryBeam = null;
        this.discoveryBeamGeometry = null;
        this.discoveryBeamState = null;
        this.islandPulse = null;
        this.islandPulseState = null;
    }
}
